import { useEffect, useRef, useState } from "react";
import type { MaterialListItem, MaterialMilestone } from "@edu/shared";
import { useMediaUrl } from "../media/media-url.ts";
import { Dialog } from "../shell/Dialog.tsx";
import { milestoneAt, shouldSend } from "./material-progress.ts";
import { FILE_GONE_TEXT } from "./my-material-text.ts";
import { sendProgress } from "./my-materials-api.ts";

/**
 * Просмотр присланного файла внутри продукта (`docs/material-receipt-design.md`,
 * часть А, шаг 4).
 *
 * Та же механика, что у медиа со слайдов и у картинки задания: адрес
 * подписанный, берётся `useMediaUrl` после проверки прав на сервере
 * (`requireReadableFile`, ветка «файл выдан материалом») и живёт минуты. Окно
 * то же, что у просмотра в медиатеке (`Dialog` с шириной `media` и класс
 * `mediaview`), но сам `MediaPlayer` не переиспользуется: он живёт в
 * преподавательской медиатеке, принимает её `MediaFile` и тянет её модуль
 * ошибок, а экран ученика модулей преподавателя не тянет.
 *
 * ⚠️ **`src` без адреса не выставляется вовсе**, как на слайде: «файл удалён» и
 * «не отдали» это честные надписи, а не сломанный плеер.
 *
 * ⚠️ **Веха считается отправленной только после ответа сервера.** Решает
 * `shouldSend`: подтверждённая веха и веха в полёте лежат в ссылках, а не в
 * состоянии (`timeupdate` приходит несколько раз в секунду, и перерисовка на
 * каждое была бы работой ради ничего). Та же веха, пока летит, второй раз не
 * шлётся; упавшая шлётся снова на следующем событии плеера. У вехи 100
 * следующего события может не быть вовсе (после `ended` плеер молчит), поэтому
 * последняя желаемая веха, так и не подтверждённая, досылается ещё раз при
 * закрытии окна. Сервер хранит большее, так что повтор ничего не портит.
 *
 * Повтор загрузки адреса это новый экземпляр тела окна (`key`), а не правка
 * хука: `useMediaUrl` берёт адрес при монтировании, а неудачу не кэширует,
 * поэтому новое тело просит подпись заново.
 */
export function MaterialViewer(props: { item: MaterialListItem; onClose: () => void }) {
  const { item } = props;
  const [attempt, setAttempt] = useState(0);
  /** Наибольшая веха, подтверждённая сервером. */
  const confirmed = useRef<MaterialMilestone | 0>(0);
  /** Веха, запрос по которой ещё летит, либо 0. */
  const inFlight = useRef<MaterialMilestone | 0>(0);
  /** Наибольшая веха, до которой дошёл плеер. */
  const wanted = useRef<MaterialMilestone | 0>(0);

  const send = (at: MaterialMilestone) => {
    inFlight.current = at;
    sendProgress(item.id, at)
      .then(() => {
        if (at > confirmed.current) confirmed.current = at;
      })
      // Отказ вехи ученику не показываем: смотреть она не мешает
      .catch(() => undefined)
      .finally(() => {
        if (inFlight.current === at) inFlight.current = 0;
      });
  };

  const onTime = (e: React.SyntheticEvent<HTMLMediaElement>) => {
    const el = e.currentTarget;
    const at = e.type === "ended" ? 100 : milestoneAt(el.currentTime, el.duration);
    if (at !== null && at > wanted.current) wanted.current = at;
    if (shouldSend(at, confirmed.current, inFlight.current)) send(at);
  };

  // Досылка при закрытии: разбор в шапке. Ссылки читаются в момент закрытия
  useEffect(
    () => () => {
      const at = wanted.current === 0 ? null : wanted.current;
      if (shouldSend(at, confirmed.current, inFlight.current)) {
        sendProgress(item.id, at).catch(() => undefined);
      }
    },
    [item.id],
  );

  return (
    <Dialog title={item.title} media onClose={props.onClose}>
      <ViewerBody
        key={attempt}
        item={item}
        onTime={onTime}
        onRetry={() => setAttempt((n) => n + 1)}
      />
    </Dialog>
  );
}

function ViewerBody(props: {
  item: MaterialListItem;
  onTime: (e: React.SyntheticEvent<HTMLMediaElement>) => void;
  onRetry: () => void;
}) {
  const { item, onTime } = props;
  const media = useMediaUrl(item.fileId);

  return (
    <>
      {media.status === "loading" && <p className="muted">Готовим файл…</p>}
      {(item.fileId === null || media.status === "gone") && <p className="muted">{FILE_GONE_TEXT}</p>}
      {media.status === "denied" && <p className="muted">Файл больше недоступен</p>}
      {/*
        Хук здесь не различает обрыв связи и отказ сервера, поэтому текст не
        называет причину: «проверьте интернет» при пятисотке отправил бы
        человека чинить то, что у него работает
      */}
      {media.status === "error" && (
        <>
          <p className="err">Не получилось открыть файл.</p>
          <p className="acts">
            <button type="button" className="btn" onClick={props.onRetry}>
              Попробовать ещё раз
            </button>
          </p>
        </>
      )}

      {media.url !== null && item.fileKind === "video" && (
        // controls обязателен: на iOS воспроизведение со звуком требует жеста
        <video
          className="mediaview"
          src={media.url}
          controls
          playsInline
          preload="metadata"
          onTimeUpdate={onTime}
          onEnded={onTime}
        />
      )}
      {media.url !== null && item.fileKind === "audio" && (
        <audio
          className="mediaview"
          src={media.url}
          controls
          preload="metadata"
          onTimeUpdate={onTime}
          onEnded={onTime}
        />
      )}
      {media.url !== null && item.fileKind === "image" && (
        <img className="mediaview" src={media.url} alt={item.title} />
      )}
      {/*
        Документ сервер отдаёт только вложением (`routes/media/content.ts`):
        показывать PDF на своём origin мы не берёмся. Поэтому здесь не
        просмотр, а скачивание, и кнопка говорит именно это.
      */}
      {media.url !== null && (item.fileKind === "doc" || item.fileKind === null) && (
        <p className="acts">
          <a className="btn btn--primary" href={media.url} download>
            Скачать
          </a>
        </p>
      )}
    </>
  );
}
